import { Component, AfterViewInit, inject, OnInit, signal, effect, untracked } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { MessageService } from 'primeng/api';

import { NgxSpinnerComponent } from 'ngx-spinner';

import { TenantsService } from '@admin/tenant/services/tenant.service';
import { UpdateService } from '@core/services/update.service';
import { MobileShellService } from '@core/services/mobile-shell.service';
import { MobileUpdaterService } from '@core/services/mobile-updater.service';
import { DesktopUpdaterService } from '@core/services/desktop-updater.service';
import { AuthService } from '@auth/services/auth.service';
import { ROLLBAR } from '@core/services/rollbar.service';
import { reportStackDepthBaseline } from '@core/utils';
import { isPackagedApp } from '@shared/utils/platform.utils';
import { getStoredTenantDomain } from '@shared/utils/domain.utils';
import { KEREP_PRODUCT } from '@shared/constants/brand.constants';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterModule, NgxSpinnerComponent, ToastModule, ButtonModule, DialogModule, ProgressBarModule],
  template: `
    <p-toast />
    <p-toast position="top-center" key="update-toast" [baseZIndex]="5000">
      <ng-template let-message #headless>
        <section class="flex flex-col p-4 gap-4 w-full bg-primary/70 rounded-xl">
          <div class="flex items-center gap-5">
            <i class="pi pi-sync text-white dark:text-black text-2xl"></i>
            <span class="font-bold text-base text-white dark:text-black">{{ message.summary }}</span>
          </div>
          <div class="flex items-center gap-5">
            <p class="text-sm text-gray-300 mt-1">{{ message.detail }}</p>
          </div>
          <div class="flex gap-4 mb-4 justify-end">
            <p-button label="Actualizar ahora" (onClick)="runUpdate()" size="small" />
          </div>
        </section>
      </ng-template>
    </p-toast>
    <p-toast position="top-center" key="mobile-update-toast" [baseZIndex]="5000">
      <ng-template let-message #headless>
        <section class="flex flex-col p-4 gap-4 w-full bg-primary/70 rounded-xl">
          <div class="flex items-center gap-5">
            <i class="pi pi-sync pi-spin text-white dark:text-black text-2xl"></i>
            <span class="font-bold text-base text-white dark:text-black">{{ message.summary }}</span>
          </div>
          <div class="flex items-center gap-5">
            <p class="text-sm text-gray-300 mt-1">{{ message.detail }}</p>
          </div>
          <div class="flex gap-4 mb-4 justify-end">
            <p-button label="Actualizar ahora" (onClick)="runMobileUpdate()" size="small" />
          </div>
        </section>
      </ng-template>
    </p-toast>
    <p-toast position="top-center" key="desktop-update-toast" [baseZIndex]="5000">
      <ng-template let-message #headless>
        <section class="flex flex-col p-4 gap-4 w-full bg-primary/70 rounded-xl">
          <div class="flex items-center gap-5">
            <i class="pi pi-sync text-white dark:text-black text-2xl"></i>
            <span class="font-bold text-base text-white dark:text-black">{{ message.summary }}</span>
          </div>
          <div class="flex items-center gap-5">
            <p class="text-sm text-gray-300 mt-1">{{ message.detail }}</p>
          </div>
          <div class="flex gap-4 mb-4 justify-end">
            <p-button label="Actualizar ahora" (onClick)="runDesktopUpdate()" size="small" />
          </div>
        </section>
      </ng-template>
    </p-toast>
    <p-dialog
      [visible]="desktopUpdater.applying()"
      [modal]="true"
      [closable]="false"
      [draggable]="false"
      [resizable]="false"
      header="Actualizando Kerep"
      [style]="{ width: '24rem' }"
    >
      <div class="flex flex-col items-center gap-4 py-2">
        <i class="pi pi-sync pi-spin text-4xl text-primary"></i>
        <p class="text-center text-sm">Instalando actualización…</p>
        <p-progressBar mode="indeterminate" [style]="{ width: '100%', height: '6px' }" />
        <p class="text-center text-xs text-surface-500">La app se reiniciará automáticamente al terminar.</p>
      </div>
    </p-dialog>
    <p-dialog
      [visible]="mobileUpdater.downloading() || mobileUpdater.applying()"
      [modal]="true"
      [closable]="false"
      [draggable]="false"
      [resizable]="false"
      header="Actualizando Kerep"
      [style]="{ width: '24rem' }"
    >
      <div class="flex flex-col items-center gap-4 py-2">
        <i class="pi pi-sync pi-spin text-4xl text-primary"></i>
        @if (mobileUpdater.applying()) {
          <p class="text-center text-sm">Aplicando actualización…</p>
          <p-progressBar mode="indeterminate" [style]="{ width: '100%', height: '6px' }" />
        } @else {
          <p class="text-center text-sm">Descargando versión {{ mobileUpdater.availableVersion() }}…</p>
          <p-progressBar [value]="mobileUpdater.downloadProgress()" [style]="{ width: '100%', height: '6px' }" />
        }
        <p class="text-center text-xs text-surface-500">La app se actualizará automáticamente al completar.</p>
      </div>
    </p-dialog>
    <ngx-spinner [fullScreen]="true" template="{{ spinnerTemplate }}"> </ngx-spinner>
    @if (!isLoading()) {
      <router-outlet></router-outlet>
    }
  `,
})
export class AppComponent implements AfterViewInit, OnInit {
  private tenantAssetsService = inject(TenantsService);
  private updateService = inject(UpdateService);
  private mobileShellService = inject(MobileShellService);
  private authService = inject(AuthService);
  private messageService = inject(MessageService);

  /** Expuesto al template para el diálogo de progreso y las señales del updater. */
  protected readonly mobileUpdater = inject(MobileUpdaterService);
  protected readonly desktopUpdater = inject(DesktopUpdaterService);
  private readonly rollbar = inject(ROLLBAR);

  isLoading = signal(true);

  /**
   * Mismo splash de marca que el arranque (`index.html`): el isotipo se dibuja
   * solo. Las clases `kerep-splash*` viven en el `<style>` crítico de
   * `index.html`, así que están disponibles antes de que arranque Angular.
   */
  get spinnerTemplate(): string {
    return `<div class="kerep-splash" id="loader-global2">
				<svg class="kerep-splash__mark" viewBox="0 0 48 48" role="img" aria-label="Kerep">
					<path class="kerep-splash__mountain" d="M6 40l6-4 4-13h3v-7h10v-4h5" pathLength="100" />
					<path class="kerep-splash__fall" d="M30 12v17c0 4.5 2.6 6.6 6.6 7.6 3 .8 5.4.6 7.4 1.4" pathLength="100" />
				</svg>
				<div class="kerep-splash__wordmark">kerep<span>${KEREP_PRODUCT}</span></div>
				<p class="loader-global-message">Verificando sesión…</p>
			</div>`;
  }

  constructor() {
    // Cuando MobileUpdaterService detecta una versión nueva, lanzar el toast de aviso.
    // `untracked` en la versión: la única dependencia del effect es `updateAvailable`, para que
    // un cambio posterior de `availableVersion` no vuelva a publicar el mismo toast.
    effect(() => {
      if (this.mobileUpdater.updateAvailable()) {
        untracked(() => this.showMobileUpdateToast());
      }
    });

    // Igual para escritorio (Tauri): la descarga ya terminó cuando `updateAvailable` pasa a true.
    // Si pasa a false (un fallo de instalación descartó la descarga) el aviso quedaría apuntando a
    // algo que ya no existe: se retira, y la siguiente descarga fresca lo publica de nuevo.
    effect(() => {
      if (this.desktopUpdater.updateAvailable()) {
        untracked(() => this.showDesktopUpdateToast());
      } else {
        untracked(() => this.messageService.clear('desktop-update-toast'));
      }
    });

    // Si la instalación arranca sola (inactividad), el aviso pendiente deja de tener sentido.
    effect(() => {
      if (this.desktopUpdater.applying()) {
        untracked(() => this.messageService.clear('desktop-update-toast'));
      }
    });
  }

  async ngOnInit() {
    this.updateService.init();
    // No-op fuera de Tauri o con el flag apagado: re-chequeo de versión con la app abierta.
    void this.desktopUpdater.init();
    // No-op en web (isCapacitor() false): plugins nativos base (botón atrás, status bar, splash).
    void this.mobileShellService.init();

    // En una app empaquetada sin empresa elegida aún NO hay dominio que resolver
    // (dispararía un 404 de tenant); el selector se encargará al elegir empresa.
    if (!isPackagedApp() || getStoredTenantDomain()) {
      await this.tenantAssetsService.getTenantIdByDomain();
    }

    // Verificar la sesión antes de mostrar la app: mientras se valida el token (refresh + datos
    // del usuario) se mantiene el loader global. Así, en el arranque en frío no se ve una pantalla
    // en blanco ni una página a medias; si la verificación falla, los guards redirigen a login.
    await this.authService.checkStatus();

    this.isLoading.set(false);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const loader = document.getElementById('loader-global');
      if (loader) {
        loader.remove();
      }
    }, 10);

    // Línea base de profundidad de pila (BUG-IOS-01). En reposo NO hace nada:
    // `STACK_PROBE_SAMPLE_RATE` vale 0 y ni siquiera se llega a medir. Se sube esa constante
    // durante una campaña de medición, para tener con qué comparar los reportes que llegan de
    // iPhone. Va en un `setTimeout` a propósito: la sonda agota la pila a posta y hay que medirla
    // desde una tarea nueva, con la pila vacía, o la cifra sale falseada a la baja.
    setTimeout(() => reportStackDepthBaseline(this.rollbar));
  }

  /** Vía manual del update de Service Worker (PWA/web). */
  runUpdate() {
    this.updateService.activateUpdate();
  }

  /**
   * Vía manual del update móvil (Capacitor). Cierra el toast y lanza la descarga con progreso.
   *
   * Si el intento falla, `applyUpdate()` devuelve `false` y se vuelve a publicar el toast: sin
   * esto el usuario se quedaba sin vía de reintento hasta reiniciar la app (el effect no vuelve
   * a dispararse porque `updateAvailable` nunca cambió de valor).
   */
  async runMobileUpdate() {
    this.messageService.clear('mobile-update-toast');
    const applied = await this.mobileUpdater.applyUpdate();
    if (!applied) {
      this.showMobileUpdateToast();
    }
  }

  /**
   * Vía manual del update de escritorio (Tauri): cierra el toast e instala + relanza. Si falla,
   * el servicio avisa con su propio toast de error y descarta la descarga: no se vuelve a publicar
   * este aviso (apuntaría a algo descartado); reaparece solo cuando una descarga nueva termine.
   */
  async runDesktopUpdate() {
    this.messageService.clear('desktop-update-toast');
    await this.desktopUpdater.applyUpdate();
  }

  /** Publica el toast de actualización de escritorio ya descargada. */
  private showDesktopUpdateToast() {
    this.messageService.add({
      key: 'desktop-update-toast',
      severity: 'info',
      summary: 'Actualización disponible',
      // Tras varios fallos ya no se instala sola: el texto no debe prometer lo que no va a pasar.
      detail: this.desktopUpdater.autoApplyEnabled
        ? `La versión ${this.desktopUpdater.availableVersion()} está lista. Se instalará cuando no estés usando la app, o puedes actualizar ahora.`
        : `La versión ${this.desktopUpdater.availableVersion()} está lista. Actualiza cuando puedas.`,
      sticky: true,
      closable: true,
    });
  }

  /** Publica el toast de "actualización disponible" con el botón de descarga. */
  private showMobileUpdateToast() {
    this.messageService.add({
      key: 'mobile-update-toast',
      severity: 'info',
      summary: 'Actualización disponible',
      detail: `La versión ${this.mobileUpdater.availableVersion()} está lista para descargar.`,
      sticky: true,
      closable: true,
    });
  }
}
